import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { EventActionCreate, EventActionPatch } from '../types'

// Per-event Actions register (spec 30). Cache-keyed by ['actions',
// eventId]; create / patch / delete all invalidate the same key so
// the list refetches after every mutation.

export function useEventActions(eventId: number) {
  return useQuery({
    queryKey: ['actions', eventId],
    queryFn: () => ApiClient.eventActions(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0
  })
}

export function useCreateAction(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EventActionCreate) => ApiClient.createAction(eventId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['actions', eventId] })
  })
}

// Bulk create — the multi-action quick-add panel (item 10). Creates each
// action sequentially (preserves the typed order) and invalidates once.
export function useCreateActionsBulk(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (bodies: EventActionCreate[]) => {
      const created = []
      for (const body of bodies) {
        created.push(await ApiClient.createAction(eventId, body))
      }
      return created
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['actions', eventId] })
  })
}

export function usePatchAction(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: EventActionPatch }) =>
      ApiClient.patchAction(eventId, id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['actions', eventId] })
  })
}

export function useDeleteAction(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => ApiClient.deleteAction(eventId, id).then(() => id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['actions', eventId] })
  })
}
